import {
  notEmptyString,
} from '@populateui/utils';
import {
  EOBExtractionResponseDTO,
} from 'dtos/eOBExtractionResponseDTO';
import {
  MissingClaimInfo,
} from 'dtos/missingClaimInfo';
import {
  ClaimDetailItem,
  ClaimGroup,
  DetailSection,
  ServiceLineDetailItem,
  SummaryItem,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/Discrepancy/types';
import {
  MissingServiceLineInfo,
} from 'dtos/missingServiceLineInfo';

export function getNormalizedItems<ItemType>(items?: ItemType[] | null): ItemType[] {
  return Array.isArray(items) ? items : [];
}

export function getServiceLineLabel(
  serviceCode?: string | null,
  modifiers?: string | null,
): string {
  const normalizedServiceCode = serviceCode ?? '';

  return notEmptyString(modifiers)
    ? `${normalizedServiceCode} · ${modifiers}`
    : normalizedServiceCode;
}

export function getClaimItemKey(item: MissingClaimInfo): string {
  return [
    item.claimExternalId ?? '',
    item.patientName ?? '',
    item.dateOfService ?? '',
  ].join('|');
}

export function getServiceLineGroupKey(item: MissingServiceLineInfo): string {
  return [
    item.claimExternalId ?? '',
    item.patientName ?? '',
    item.dateOfService ?? '',
  ].join('|');
}

export function getClaimsMissingInSystemItems(items: MissingClaimInfo[]): ClaimDetailItem[] {
  return items.map((item) => ({
    key: getClaimItemKey(item),
    claimExternalId: item?.claimExternalId ?? '',
    patientName: item?.patientName ?? '',
    dateOfService: item?.dateOfService ?? '',
  }));
}

export function groupServiceLinesByClaim(items: MissingServiceLineInfo[]): ServiceLineDetailItem[] {
  const groupedItems = items.reduce<Record<string, ClaimGroup>>((acc, item) => {
    const key = getServiceLineGroupKey(item);

    if (!acc[key]) {
      acc[key] = {
        key,
        claimExternalId: item?.claimExternalId ?? '',
        patientName: item?.patientName ?? '',
        dateOfService: item?.dateOfService ?? '',
        serviceLines: [],
      };
    }

    acc[key].serviceLines.push(getServiceLineLabel(item.serviceCode, item.modifiers));
    return acc;
  }, {});

  return Object.values(groupedItems);
}

export function getSummaryItems(
  parsedEOBErrors?: Omit<EOBExtractionResponseDTO, 'eobWithInsurancePayment'>,
): SummaryItem[] {
  const claimsPresentInEobButMissingInSystem = getNormalizedItems(
    parsedEOBErrors?.claimsPresentInEobButMissingInSystem,
  );
  const serviceLinesPresentInEobButMissingInClaim = getNormalizedItems(
    parsedEOBErrors?.serviceLinesPresentInEobButMissingInClaim,
  );
  const serviceLinesPresentInClaimButMissingInEob = getNormalizedItems(
    parsedEOBErrors?.serviceLinesPresentInClaimButMissingInEob,
  );

  return [
    {
      key: 'payerMissing',
      label: 'Payer not matched in system',
      count: parsedEOBErrors?.isPayerMissingInSystem ? 1 : 0,
    },
    {
      key: 'claimsPresentInEobButMissingInSystem',
      label: 'Missing claims',
      count: claimsPresentInEobButMissingInSystem.length,
    },
    {
      key: 'serviceLinesPresentInEobButMissingInClaim',
      label: 'EOB service lines missing in claims',
      count: serviceLinesPresentInEobButMissingInClaim.length,
    },
    {
      key: 'serviceLinesPresentInClaimButMissingInEob',
      label: 'Claim service lines missing in EOB',
      count: serviceLinesPresentInClaimButMissingInEob.length,
    },
  ].filter((item) => item.count > 0);
}

export function getDetailSections(
  parsedEOBErrors?: Omit<EOBExtractionResponseDTO, 'eobWithInsurancePayment'>,
): DetailSection[] {
  const payerMissingSection: DetailSection[] = parsedEOBErrors?.isPayerMissingInSystem
    ? [{
      key: 'payerMissing',
      title: 'Payer not matched in system',
      count: 1,
      items: [],
      variant: 'payer',
    }]
    : [];
  const claimsPresentInEobButMissingInSystem = getNormalizedItems(
    parsedEOBErrors?.claimsPresentInEobButMissingInSystem,
  );
  const serviceLinesPresentInEobButMissingInClaim = getNormalizedItems(
    parsedEOBErrors?.serviceLinesPresentInEobButMissingInClaim,
  );
  const serviceLinesPresentInClaimButMissingInEob = getNormalizedItems(
    parsedEOBErrors?.serviceLinesPresentInClaimButMissingInEob,
  );

  const detailSections: DetailSection[] = [
    ...payerMissingSection,
    {
      key: 'claimsPresentInEobButMissingInSystem',
      title: 'Claims missing in system',
      count: claimsPresentInEobButMissingInSystem.length,
      items: getClaimsMissingInSystemItems(claimsPresentInEobButMissingInSystem),
      variant: 'claim',
    },
    {
      key: 'serviceLinesPresentInEobButMissingInClaim',
      title: 'EOB service lines missing in claim',
      count: serviceLinesPresentInEobButMissingInClaim.length,
      items: groupServiceLinesByClaim(serviceLinesPresentInEobButMissingInClaim),
      variant: 'service-line',
    },
    {
      key: 'serviceLinesPresentInClaimButMissingInEob',
      title: 'Claim service lines missing in EOB',
      count: serviceLinesPresentInClaimButMissingInEob.length,
      items: groupServiceLinesByClaim(serviceLinesPresentInClaimButMissingInEob),
      variant: 'service-line',
    },
  ];

  return detailSections.filter((section) => section.count > 0);
}
